/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../contexts/ThemeContext';
import { UseGeminiLiveReturn } from '../../hooks/useGeminiLive';

interface GeminiLiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  live: UseGeminiLiveReturn;
  /** Example phrases shown above the visualizer (prospect demo). */
  hints?: string[];
  /** Auto-disconnect after this many seconds, with a visible countdown. */
  maxSessionSeconds?: number;
}

const GeminiLiveModal: React.FC<GeminiLiveModalProps> = ({ isOpen, onClose, live, hints, maxSessionSeconds }) => {
  const { t } = useTranslation();
  const { theme } = useThemeContext();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  // Optional session cap (demo): a forgotten open mic must not burn Live API
  // minutes. Counts from when the modal opened.
  const [secondsLeft, setSecondsLeft] = useState<number | null>(maxSessionSeconds ?? null);
  useEffect(() => {
    if (!isOpen || !maxSessionSeconds) return;
    const openedAt = Date.now();
    setSecondsLeft(maxSessionSeconds);
    const id = window.setInterval(() => {
      const left = maxSessionSeconds - Math.floor((Date.now() - openedAt) / 1000);
      setSecondsLeft(Math.max(0, left));
      if (left <= 0) {
        window.clearInterval(id);
        onClose();
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [isOpen, maxSessionSeconds, onClose]);

  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [live.modelTranscript]);

  // Audio Visualizer Logic
  useEffect(() => {
    if (!isOpen || !live.analyserNode || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext('2d');
    if (!canvasCtx) return;

    const analyser = live.analyserNode;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);

      analyser.getByteFrequencyData(dataArray);

      const WIDTH = canvas.width;
      const HEIGHT = canvas.height;

      canvasCtx.clearRect(0, 0, WIDTH, HEIGHT);

      // Draw background (optional, can be transparent)
      // canvasCtx.fillStyle = 'rgba(255, 255, 255, 0.05)';
      // canvasCtx.fillRect(0, 0, WIDTH, HEIGHT);

      const barWidth = (WIDTH / bufferLength) * 2.5;
      let barHeight;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        barHeight = dataArray[i] / 2;

        const r = barHeight + 25 * (i / bufferLength);
        const g = 250 * (i / bufferLength);
        const b = 50;

        // Use theme primary color if available, else dynamic
        if (theme?.colors?.primary) {
             canvasCtx.fillStyle = theme.colors.primary;
             // Add some opacity based on volume
             canvasCtx.globalAlpha = Math.min(1, barHeight / 100 + 0.2);
        } else {
             canvasCtx.fillStyle = `rgb(${r},${g},${b})`;
             canvasCtx.globalAlpha = 1;
        }

        canvasCtx.fillRect(x, HEIGHT - barHeight, barWidth, barHeight);

        x += barWidth + 1;
      }
    };

    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isOpen, live.analyserNode, theme]);

  if (!isOpen) return null;

  // Localized, actionable message per failure type (raw browser/SDK strings
  // are English-only and not actionable for guardians).
  const errorMessage = ((): string | null => {
    switch (live.errorCode) {
      case 'MIC_PERMISSION_DENIED':
        return `${t('liveHelp.microphoneAccessDenied')} ${t('liveHelp.micBlockedHelp')}`;
      case 'MIC_UNAVAILABLE':
        return t('liveHelp.micUnavailable');
      case 'UNSUPPORTED':
        return t('liveHelp.unsupported');
      case 'RATE_LIMITED':
        return t('liveHelp.rateLimited');
      case 'CONNECTION_FAILED':
        return t('liveHelp.connectionFailed');
      case 'SESSION_CLOSED':
        return t('liveHelp.sessionEnded');
      default:
        return live.status === 'ERROR' ? live.error || t('liveHelp.errorOccurred') : null;
    }
  })();

  // A session can always be restarted from the modal, without closing it or
  // reloading the page. (No retry when the browser cannot do Live audio at all.)
  const canRetry =
    (live.status === 'ERROR' || live.status === 'IDLE') && live.errorCode !== 'UNSUPPORTED';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col items-center p-6 relative h-[75vh]">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={t('common.close')}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header / Status */}
        <div className="text-center mb-8">
          <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50">
             {/* Dynamic Icon based on status */}
             {live.status === 'CONNECTING' && (
                 <svg className="w-8 h-8 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                 </svg>
             )}
             {live.status === 'LISTENING' && (
                 <svg className="w-8 h-8 text-blue-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                 </svg>
             )}
             {live.status === 'SPEAKING' && (
                 <svg className="w-8 h-8 text-green-500 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M11 5L6 9H2v6h4l5 4V5z" />
                 </svg>
             )}
             {live.status === 'ERROR' && (
                 <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                 </svg>
             )}
          </div>
          <h2 className="text-xl font-semibold text-gray-900" aria-live="polite">
            {live.status === 'CONNECTING' && t('liveHelp.connecting', 'Connecting...')}
            {live.status === 'LISTENING' && t('liveHelp.listening', 'Listening...')}
            {live.status === 'SPEAKING' && t('liveHelp.speaking', 'Speaking...')}
            {live.status === 'ERROR' && t('liveHelp.error', 'Error Occurred')}
          </h2>
          {errorMessage && (
            <p
              className={`mt-2 text-sm ${live.status === 'ERROR' ? 'text-red-600' : 'text-gray-600'}`}
              role={live.status === 'ERROR' ? 'alert' : 'status'}
              data-testid="live-help-error"
            >
              {errorMessage}
            </p>
          )}
        </div>

        {hints && hints.length > 0 && (
          <div className="w-full mb-4 text-sm text-gray-700" data-testid="live-help-hints">
            <p className="font-medium mb-1">{t('liveHelp.trySaying', 'Try saying:')}</p>
            <ul className="list-disc ps-5 space-y-0.5">
              {hints.map(h => <li key={h}>“{h}”</li>)}
            </ul>
          </div>
        )}

        {/* Visualizer */}
        <div className="w-full flex-1 min-h-0 mb-6 flex items-center justify-center bg-gray-50 rounded-lg overflow-hidden border border-gray-100 relative">
            {live.status === 'LISTENING' || live.status === 'SPEAKING' ? (
                 <>
                   <canvas
                     ref={canvasRef}
                     width={300}
                     height={128}
                     className="w-full h-full"
                   />
                   {/* Transcript Overlay */}
                   {live.modelTranscript ? (
                     <div className="absolute inset-0 flex items-start justify-center p-4 bg-black/40 text-white text-sm font-medium overflow-y-auto z-10">
                        <div className="w-full text-left prose prose-invert prose-sm max-w-none">
                          <ReactMarkdown>
                            {live.modelTranscript}
                          </ReactMarkdown>
                          <div ref={transcriptEndRef} />
                        </div>
                     </div>
                   ) : (
                    <div className="absolute inset-0 flex items-center justify-center p-4 text-gray-500 text-center text-sm font-medium overflow-y-auto z-10">
                      {t('liveHelp.askAnything', 'Ask anything to get started')}
                    </div>
                   )}
                 </>
            ) : (
                <div className="text-gray-400 text-sm">
                    {live.status === 'CONNECTING'
                      ? t('liveHelp.connecting', 'Connecting...')
                      : t('liveHelp.readyToConnect', 'Ready to connect')}
                </div>
            )}
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-4 w-full">
          {canRetry && (
            <button
              type="button"
              // Called synchronously from the click so the hook can unlock
              // audio + request the mic inside the user gesture (Safari).
              onClick={() => void live.connect()}
              data-testid="live-help-retry-button"
              className="flex-1 py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {t('common.retry', 'Try Again')}
            </button>
          )}
          {live.errorCode === 'MIC_PERMISSION_DENIED' && (
            <button
              type="button"
              // Safari keeps a per-page "deny" until reload even after the
              // site setting is changed; a reload is the guaranteed recovery.
              onClick={() => window.location.reload()}
              data-testid="live-help-reload-button"
              className="flex-1 py-3 px-4 bg-gray-100 text-gray-800 font-medium rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
            >
              {t('liveHelp.reloadPage', 'Reload page')}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            data-testid="live-help-end-button"
            className="flex-1 py-3 px-4 bg-red-50 text-red-700 font-medium rounded-lg hover:bg-red-100 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            {t('liveHelp.endSession', 'End Session')}
          </button>
        </div>
        <p className="text-xs text-center text-gray-500 mt-4">
          {t('liveHelp.multilingualNote')}
        </p>
        {secondsLeft !== null && (
          <p className="text-xs text-center text-gray-500 mt-1 tabular-nums" aria-live="off">
            {t('liveHelp.sessionEndsIn', {
              defaultValue: 'Session ends in {{time}}',
              time: `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`,
            })}
          </p>
        )}
      </div>
    </div>
  );
};

export default GeminiLiveModal;
